import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Textarea,
} from '@rolefit/ui';
import { queries } from '../lib/queries.js';
import { useAsync } from '../lib/useAsync.js';

interface ParsedRole {
  title?: string;
  org?: string;
  start?: string | null;
  end?: string | null;
  bullets?: string[];
}
interface ParsedEducation {
  degree?: string | null;
  institution?: string | null;
  year?: string | null;
}
interface ParsedProfile {
  roles?: ParsedRole[];
  skills?: string[];
  education?: ParsedEducation[];
  location?: string | null;
}

// Editable review of the parsed profile. The user fixes any parse errors, then
// confirms; the generator may not cite any fact until confirmed.
function ProfileEditor({ value, onChange }: { value: ParsedProfile; onChange: (p: ParsedProfile) => void }) {
  const roles = value.roles ?? [];
  const skills = value.skills ?? [];
  const education = value.education ?? [];
  const [newSkill, setNewSkill] = useState('');

  const patchRole = (i: number, p: Partial<ParsedRole>) =>
    onChange({ ...value, roles: roles.map((r, j) => (j === i ? { ...r, ...p } : r)) });
  const patchEdu = (i: number, p: Partial<ParsedEducation>) =>
    onChange({ ...value, education: education.map((e, j) => (j === i ? { ...e, ...p } : e)) });

  return (
    <div className="space-y-6 rounded-md border bg-muted/30 p-4">
      <div className="space-y-1">
        <Label htmlFor="loc">Location</Label>
        <Input
          id="loc"
          value={value.location ?? ''}
          onChange={(e) => onChange({ ...value, location: e.target.value })}
        />
      </div>

      <section className="space-y-3">
        <h3 className="text-sm font-medium">Experience</h3>
        {roles.map((r, i) => (
          <div key={i} className="space-y-2 rounded-md border bg-background p-3">
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Title"
                value={r.title ?? ''}
                onChange={(e) => patchRole(i, { title: e.target.value })}
              />
              <Input
                placeholder="Organization"
                value={r.org ?? ''}
                onChange={(e) => patchRole(i, { org: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Start"
                value={r.start ?? ''}
                onChange={(e) => patchRole(i, { start: e.target.value })}
              />
              <Input placeholder="End" value={r.end ?? ''} onChange={(e) => patchRole(i, { end: e.target.value })} />
            </div>
            <Textarea
              rows={3}
              placeholder="One bullet per line"
              value={(r.bullets ?? []).join('\n')}
              onChange={(e) => patchRole(i, { bullets: e.target.value.split('\n').filter((b) => b.trim()) })}
            />
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-destructive"
              onClick={() => onChange({ ...value, roles: roles.filter((_, j) => j !== i) })}
            >
              Remove role
            </button>
          </div>
        ))}
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium">Skills</h3>
        <div className="flex flex-wrap gap-1.5">
          {skills.map((s, i) => (
            <Badge key={i} variant="secondary" className="gap-1">
              {s}
              <button
                type="button"
                aria-label={`Remove ${s}`}
                className="text-muted-foreground hover:text-destructive"
                onClick={() => onChange({ ...value, skills: skills.filter((_, j) => j !== i) })}
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Add a skill"
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newSkill.trim()) {
                e.preventDefault();
                onChange({ ...value, skills: [...skills, newSkill.trim()] });
                setNewSkill('');
              }
            }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (newSkill.trim()) {
                onChange({ ...value, skills: [...skills, newSkill.trim()] });
                setNewSkill('');
              }
            }}
          >
            Add
          </Button>
        </div>
      </section>

      {education.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-medium">Education</h3>
          {education.map((e, i) => (
            <div key={i} className="grid grid-cols-3 gap-2">
              <Input
                placeholder="Degree"
                value={e.degree ?? ''}
                onChange={(ev) => patchEdu(i, { degree: ev.target.value })}
              />
              <Input
                placeholder="Institution"
                value={e.institution ?? ''}
                onChange={(ev) => patchEdu(i, { institution: ev.target.value })}
              />
              <Input
                placeholder="Year"
                value={e.year ?? ''}
                onChange={(ev) => patchEdu(i, { year: ev.target.value })}
              />
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

export function OnboardPage() {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const existing = useAsync(() => queries.getProfile(), []);
  const [draft, setDraft] = useState<ParsedProfile | null>(null);
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill the editor with the current profile so a returning user edits it in
  // place; importing a CV or URL replaces the draft.
  useEffect(() => {
    if (existing.data?.structured && !draft) setDraft(existing.data.structured as ParsedProfile);
  }, [existing.data]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onFile(file: File) {
    setBusy(true);
    setError(null);
    try {
      const p = await queries.importCv(file);
      setDraft((p.structured as ParsedProfile) ?? {});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  }

  async function onUrl() {
    if (!url.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const p = await queries.importCvUrl(url.trim());
      setDraft((p.structured as ParsedProfile) ?? {});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  }

  async function onText() {
    if (!text.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const p = await queries.importCvText(text);
      setDraft((p.structured as ParsedProfile) ?? {});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    setBusy(true);
    try {
      await queries.confirmProfile(draft);
      navigate('/', { replace: true });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">{existing.data?.confirmed ? 'Your profile' : 'Set up your profile'}</h1>
        <p className="text-sm text-muted-foreground">
          Import your CV or a profile URL, fix anything that parsed wrong, then confirm. Re-importing replaces the draft
          below.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Import a CV</CardTitle>
          <CardDescription>Upload a file (PDF, DOCX, or text, up to 10 MB) or import from a URL.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx,.txt,.md"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
            }}
          />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}>
            {busy && !draft ? 'Working…' : draft ? 'Choose a different file' : 'Choose file'}
          </Button>

          <div className="space-y-1.5">
            <Label htmlFor="profile-url" className="text-sm">
              Or import from a URL
            </Label>
            <div className="flex gap-2">
              <Input
                id="profile-url"
                type="url"
                placeholder="https://your-site.com/resume or a public profile URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void onUrl();
                  }
                }}
              />
              <Button variant="outline" onClick={onUrl} disabled={busy || !url.trim()}>
                Import
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Personal sites and public resume pages work. LinkedIn blocks automated reading: open your profile, choose
              More, then Save to PDF, and upload that file above, or paste the text below.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="profile-text" className="text-sm">
              Or paste your profile / resume text
            </Label>
            <Textarea
              id="profile-text"
              rows={6}
              placeholder="Open your LinkedIn profile (or resume), select all, and paste it here."
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <div className="flex justify-end">
              <Button variant="outline" onClick={onText} disabled={busy || !text.trim()}>
                Import pasted text
              </Button>
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {draft && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Review and edit what we extracted, then confirm.</p>
              <ProfileEditor value={draft} onChange={setDraft} />
              <div className="flex items-center gap-3">
                <Button onClick={confirm} disabled={busy}>
                  Confirm profile
                </Button>
                <span className="text-xs text-muted-foreground">
                  Confirming unlocks scoring and package generation.
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
